import React from 'react';
import { colors, fonts, sectionLabelStyle, gradients } from './styles';
import { Highlight } from './Highlight';

const ModeBadge = ({ mode }) => {
  const isLive = mode === 'Live';
  const isAsync = mode === 'Async';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontFamily: fonts.sans,
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        color: colors.deepCoral,
        marginBottom: '10px',
      }}
    >
      {isLive && (
        <span
          aria-hidden="true"
          className="pi-live-dot"
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: colors.terracotta,
            boxShadow: `0 0 0 0 ${colors.terracotta}`,
            display: 'inline-block',
          }}
        />
      )}
      {isAsync && (
        <span aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ width: '12px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
          <span style={{ width: '8px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
          <span style={{ width: '10px', height: '2px', borderRadius: '1px', backgroundColor: colors.coral }} />
        </span>
      )}
      {!isLive && !isAsync && (
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: colors.terracotta }} />
          <span style={{ width: '1px', height: '8px', backgroundColor: colors.coralMid }} />
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '1.5px' }}>
            <span style={{ width: '8px', height: '1.5px', backgroundColor: colors.coral }} />
            <span style={{ width: '6px', height: '1.5px', backgroundColor: colors.coral }} />
          </span>
        </span>
      )}
      {mode}
    </span>
  );
};

const methods = [
  {
    mode: 'Live',
    title: 'In-Depth Interviews',
    desc: 'Conversations surfacing rich lived experience and the language patients use to describe their condition.',
    uses: ['Lived experience', 'unmet needs', 'exploratory conversations'],
  },
  {
    mode: 'Live',
    title: 'Focus Groups & Advisory Boards',
    desc: 'Group discussions surfacing shared experiences, areas of divergence, and collective patient priorities.',
    uses: ['Community norms', 'comparative perspectives', 'co‑design sessions'],
  },
  {
    mode: 'Async',
    title: 'Protocol Reviews',
    desc: 'Evaluate trial design, schedules, data collection, feasibility, and flag opportunities to refine.',
    uses: ['De-risking trial design', 'inclusion criteria', 'identifying barriers'],
  },
  {
    mode: 'Async',
    title: 'Activity Boards',
    desc: "Structured asynchronous platform activities capturing qualitative depth at scale, on participants' own schedules.",
    uses: ['Review of recruitment materials', 'concept validation', 'patient materials'],
  },
  {
    mode: 'Async',
    title: 'Surveys',
    desc: 'Robust quantitative and qualitative surveys designed for larger cohorts. Thorough, structured, and statistically meaningful at scale.',
    uses: ['Population-level insight', 'PRO testing', 'broad pattern analysis'],
  },
  {
    mode: 'Live or Async',
    title: 'Concept & Materials Review',
    desc: 'Patients review prototypes, marketing materials, communications, or educational content and provide structured feedback.',
    uses: ['Message testing', 'patient materials', 'prototype feedback'],
  },
];

export const GoDeeperSection = () => {
  return (
    <section
      style={{
        backgroundColor: colors.white,
        padding: '96px 48px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .pi-deeper-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
          .pi-method-grid { grid-template-columns: 1fr !important; }
          .pi-deeper-callout { padding: 24px !important; }
        }
        @keyframes pi-pulse {
          0% { box-shadow: 0 0 0 0 rgba(232,146,124,0.55); }
          70% { box-shadow: 0 0 0 10px rgba(232,146,124,0); }
          100% { box-shadow: 0 0 0 0 rgba(232,146,124,0); }
        }
        .pi-live-dot { animation: pi-pulse 2s infinite; }
        .pi-method-card { transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s; }
        .pi-method-card:hover { border-color: ${colors.coralMid} !important; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(235,160,155,0.18); }
      `}</style>
      <div className="pi-deeper-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center' }}>
          <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>Going deeper</p>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: 'clamp(26px, 3vw, 40px)',
              fontWeight: 700,
              color: colors.charcoal,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            Start with a Pulse Check.
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
              Go as <Highlight>deep</Highlight> as you need.
            </span>
          </h2>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '17px',
              color: colors.warmGray,
              maxWidth: '640px',
              margin: '0 auto',
              fontWeight: 400,
              lineHeight: 1.75,
              textWrap: 'pretty',
            }}
          >
            When a signal warrants deeper investigation, our research team applies fit-for-purpose methodology to your objective.<br className="pi-br-md" />
            {' '}<span style={{ fontStyle: 'italic' }}>No handoff to a new vendor. No cold start.</span>
          </p>
        </div>
        <style>{`.pi-br-md { display: none; } @media (min-width: 768px) { .pi-br-md { display: inline; } }`}</style>

        <div
          className="pi-method-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '16px',
            maxWidth: '960px',
            margin: '0 auto 40px',
            width: '100%',
          }}
        >
          {methods.map((m) => (
            <div
              key={m.title}
              className="pi-method-card"
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                padding: '24px',
                transition: 'border-color 0.2s',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <ModeBadge mode={m.mode} />
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: colors.charcoal,
                  marginBottom: '10px',
                  lineHeight: 1.3,
                }}
              >
                {m.title}
              </div>
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '14px',
                  color: colors.warmGray,
                  lineHeight: 1.6,
                  fontWeight: 400,
                  marginBottom: '14px',
                  textWrap: 'pretty',
                }}
              >
                {m.desc}
              </div>
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '12px',
                  color: colors.midGray,
                  fontStyle: 'italic',
                  lineHeight: 1.55,
                  marginTop: 'auto',
                }}
              >
                {m.uses.map((u, i) => (
                  <React.Fragment key={u}>
                    {i > 0 && ' · '}
                    <span style={{ whiteSpace: 'nowrap' }}>{u}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className="pi-deeper-callout"
          style={{
            background: `${gradients.primarySoft}, ${colors.white}`,
            padding: '36px 40px',
            borderRadius: '14px',
            maxWidth: '720px',
            margin: '0 auto',
            textAlign: 'center',
            border: '1px solid rgba(235,160,155,0.22)',
            position: 'relative',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-14px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: gradients.coralPeach,
              color: colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(235,160,155,0.3)',
            }}
          >
            ✦
          </div>
          <p
            style={{
              fontFamily: fonts.serif,
              fontSize: '19px',
              lineHeight: 1.55,
              color: colors.charcoal,
              margin: 0,
              fontWeight: 500,
              fontStyle: 'italic',
              textWrap: 'pretty',
            }}
          >
            Pulse Checks don't replace deeper research — they make it <Highlight>more targeted and more valuable</Highlight>.
          </p>
        </div>
      </div>
    </section>
  );
};
